import React, { useEffect, useRef, useState } from 'react';
import { onboardingAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { TextT, CalendarBlank, CheckSquare, Signature, TextAa, Trash, MagnifyingGlassPlus, MagnifyingGlassMinus, FloppyDisk, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import PdfPages, { FIELD_COLORS, FIELD_LABELS, useContainerWidth } from './PdfPages';

const TOOLS = [
  { type: 'text', icon: TextT, w: 0.28, h: 0.028 },
  { type: 'date', icon: CalendarBlank, w: 0.16, h: 0.028 },
  { type: 'checkbox', icon: CheckSquare, w: 0.025, h: 0.02 },
  { type: 'signature', icon: Signature, w: 0.32, h: 0.06 },
  { type: 'initials', icon: TextAa, w: 0.09, h: 0.04 },
];

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const PdfFieldEditor = ({ template, open, onClose, onSaved }) => {
  const [fields, setFields] = useState([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [inPacket, setInPacket] = useState(true);
  const [tool, setTool] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [file, setFile] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [saving, setSaving] = useState(false);
  const pageRefs = useRef([]);
  const drag = useRef(null);
  const [containerRef, containerWidth] = useContainerWidth();
  const pageWidth = Math.max(280, Math.min(containerWidth - 32, 900) * zoom);

  useEffect(() => {
    if (!open || !template) return;
    setFields((template.fields || []).map(f => ({ ...f })));
    setName(template.name || '');
    setDescription(template.description || '');
    setInPacket(template.in_packet !== false);
    setTool(null); setSelectedId(null); setFile(null);
    onboardingAPI.templatePdf(template.id)
      .then(res => setFile(new Blob([res.data], { type: 'application/pdf' })))
      .catch(() => toast.error('Could not load PDF'));
  }, [open, template]);

  const selected = fields.find(f => f.id === selectedId);
  const updateField = (id, patch) => setFields(prev => prev.map(f => (f.id === id ? { ...f, ...patch } : f)));
  const removeField = (id) => { setFields(prev => prev.filter(f => f.id !== id)); if (selectedId === id) setSelectedId(null); };

  const pageDims = (pageIdx) => {
    const p = template.pages[pageIdx];
    return { w: pageWidth, h: pageWidth * (p.height / p.width) };
  };

  const handlePagePointerDown = (e, pageIdx) => {
    if (!tool) { setSelectedId(null); return; }
    const rect = pageRefs.current[pageIdx].getBoundingClientRect();
    const t = TOOLS.find(x => x.type === tool);
    const { w, h } = pageDims(pageIdx);
    const aspect = w / h;
    const fh = tool === 'checkbox' ? t.w * aspect : t.h;
    const x = clamp((e.clientX - rect.left) / w - t.w / 2, 0, 1 - t.w);
    const y = clamp((e.clientY - rect.top) / h - fh / 2, 0, 1 - fh);
    const id = `f_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setFields(prev => [...prev, { id, type: tool, page: pageIdx, x, y, w: t.w, h: fh, label: '', required: tool !== 'checkbox', font_size: 11 }]);
    setSelectedId(id);
    setTool(null);
  };

  const startDrag = (e, field, mode) => {
    e.stopPropagation(); e.preventDefault();
    setSelectedId(field.id);
    const { w, h } = pageDims(field.page);
    drag.current = { id: field.id, mode, startX: e.clientX, startY: e.clientY, orig: { ...field }, pw: w, ph: h };
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', endDrag);
  };
  const onDragMove = (e) => {
    const d = drag.current; if (!d) return;
    const dx = (e.clientX - d.startX) / d.pw;
    const dy = (e.clientY - d.startY) / d.ph;
    if (d.mode === 'move') {
      updateField(d.id, { x: clamp(d.orig.x + dx, 0, 1 - d.orig.w), y: clamp(d.orig.y + dy, 0, 1 - d.orig.h) });
    } else {
      updateField(d.id, { w: clamp(d.orig.w + dx, 0.015, 1 - d.orig.x), h: clamp(d.orig.h + dy, 0.01, 1 - d.orig.y) });
    }
  };
  const endDrag = () => {
    drag.current = null;
    window.removeEventListener('pointermove', onDragMove);
    window.removeEventListener('pointerup', endDrag);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Enter a form name'); return; }
    setSaving(true);
    try {
      const res = await onboardingAPI.updateTemplate(template.id, { name: name.trim(), description, in_packet: inPacket, fields });
      toast.success('Form saved');
      onSaved?.(res.data);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const renderOverlay = (pageIdx, w, h) => fields.filter(f => f.page === pageIdx).map(f => {
    const isSel = f.id === selectedId;
    return (
      <div
        key={f.id}
        className={`absolute border-2 rounded-sm cursor-move ${FIELD_COLORS[f.type]} ${isSel ? 'ring-2 ring-offset-1 ring-primary z-10' : ''}`}
        style={{ left: f.x * w, top: f.y * h, width: f.w * w, height: f.h * h }}
        onPointerDown={(e) => startDrag(e, f, 'move')}
        data-testid={`field-box-${f.id}`}
      >
        <span className="absolute -top-4 left-0 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap text-slate-700 bg-white/80 px-1 rounded">
          {f.label || FIELD_LABELS[f.type]}{f.required ? ' *' : ''}
        </span>
        <div
          className="absolute -right-1.5 -bottom-1.5 w-3.5 h-3.5 bg-primary rounded-full cursor-se-resize border-2 border-white"
          onPointerDown={(e) => startDrag(e, f, 'resize')}
        />
      </div>
    );
  });

  if (!template) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }} modal>
      <DialogContent className="max-w-[100vw] w-screen h-[100dvh] sm:h-[96vh] sm:max-w-[96vw] p-0 gap-0 flex flex-col rounded-none sm:rounded-2xl overflow-hidden" onPointerDownOutside={(e) => e.preventDefault()} data-testid="pdf-field-editor">
        <DialogHeader className="px-5 py-3 border-b border-border flex-row items-center justify-between space-y-0">
          <div className="min-w-0">
            <DialogTitle className="font-heading text-lg truncate">Place fill boxes — {name || template.name}</DialogTitle>
            <DialogDescription className="text-xs">Pick a field type, then click on the page where it belongs. Drag to move, use the corner dot to resize.</DialogDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" className="rounded-xl" onClick={onClose} data-testid="editor-cancel-btn"><X className="w-4 h-4" /></Button>
            <Button size="sm" className="rounded-xl gap-1" onClick={handleSave} disabled={saving} data-testid="editor-save-btn"><FloppyDisk weight="bold" className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Form'}</Button>
          </div>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 flex-col md:flex-row">
          {/* Toolbar */}
          <div className="md:w-64 shrink-0 border-b md:border-b-0 md:border-r border-border p-4 space-y-4 overflow-y-auto bg-muted/30">
            <div className="space-y-2">
              <Label className="text-xs">Form name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl h-9" data-testid="template-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Instructions for applicant</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" className="rounded-xl h-9" data-testid="template-desc-input" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Include in onboarding packet</Label>
              <Switch checked={inPacket} onCheckedChange={setInPacket} data-testid="template-in-packet-switch" />
            </div>

            <div>
              <Label className="text-xs">Add a field</Label>
              <div className="grid grid-cols-5 md:grid-cols-2 gap-2 mt-2">
                {TOOLS.map(t => (
                  <Button key={t.type} variant={tool === t.type ? 'default' : 'outline'} size="sm" className="rounded-xl gap-1 justify-start h-9" onClick={() => setTool(tool === t.type ? null : t.type)} data-testid={`tool-${t.type}`}>
                    <t.icon weight="duotone" className="w-4 h-4" /> <span className="hidden md:inline">{FIELD_LABELS[t.type]}</span>
                  </Button>
                ))}
              </div>
              {tool && <p className="text-[11px] text-primary mt-2 font-medium">Now click on the page to place the {FIELD_LABELS[tool].toLowerCase()} box.</p>}
            </div>

            {selected ? (
              <div className="rounded-xl border border-border bg-background p-3 space-y-3" data-testid="field-props-panel">
                <div className="flex items-center justify-between">
                  <Badge className={`rounded-full text-[10px] uppercase ${FIELD_COLORS[selected.type]} text-foreground`}>{FIELD_LABELS[selected.type]} · p{selected.page + 1}</Badge>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeField(selected.id)} data-testid="field-delete-btn"><Trash className="w-4 h-4" /></Button>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Label (shown to applicant)</Label>
                  <Input value={selected.label} onChange={(e) => updateField(selected.id, { label: e.target.value })} placeholder={`e.g. ${selected.type === 'signature' ? 'Employee signature' : 'Full legal name'}`} className="rounded-lg h-8 text-sm" data-testid="field-label-input" />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Required</Label>
                  <Switch checked={!!selected.required} onCheckedChange={(v) => updateField(selected.id, { required: v })} data-testid="field-required-switch" />
                </div>
                {(selected.type === 'text' || selected.type === 'date') && (
                  <div className="space-y-1">
                    <Label className="text-xs">Max font size (pt)</Label>
                    <Input type="number" min={6} max={24} value={selected.font_size || 11} onChange={(e) => updateField(selected.id, { font_size: Number(e.target.value) })} className="rounded-lg h-8 text-sm" />
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">{fields.length} field{fields.length !== 1 ? 's' : ''} placed. Click a box on the page to edit it.</p>
            )}
          </div>

          {/* Canvas */}
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="flex items-center justify-end gap-1 px-4 py-1.5 border-b border-border text-xs">
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}><MagnifyingGlassMinus className="w-4 h-4" /></Button>
              <span className="w-12 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setZoom(z => Math.min(3, z + 0.25))}><MagnifyingGlassPlus className="w-4 h-4" /></Button>
            </div>
            <div ref={containerRef} className={`flex-1 overflow-auto bg-slate-200/70 p-4 ${tool ? 'cursor-crosshair' : ''}`}>
              <PdfPages file={file} pages={template.pages} pageWidth={pageWidth} renderOverlay={renderOverlay} onPagePointerDown={handlePagePointerDown} pageRefs={pageRefs} />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PdfFieldEditor;
